import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaWorkplaceStepDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { workplacesToOptions } from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import {
  A3InnunvaSource,
  A3InnuvaMapping,
  A3InnuvaWizardData,
  a3InnuvaSourceSchema,
} from 'modules/payroll_integrations_a3innuva/lib/types'

import { EmployeeGroupsByLegalEntity } from './EmployeeGroupsByLegalEntity'

const a3InnuvaWorkplaceSchema = z.object({
  source: z.string().optional(),
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        a3innuva_id: z.string(),
        meta: z.object({
          uuid: z.string(),
        }),
      })
    )
    .optional(),
})

const WorkplaceStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const workplaces: A3InnunvaSource = data.sources?.workplaces || {}

  useUnsafeEffect(() => {
    /**
     * This is a hack to be able to manage structured data.
     * Initially the data was thought to be a hash with both the key and the value as strings.
     * This data type is causing a lot of limitations and we are trying to improve it.
     * */
    if (data.sources?.workplaces === undefined && data.answers.workplaces !== undefined) {
      const parsedData = a3InnuvaWorkplaceSchema.safeParse(data.answers.workplaces)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          workplaces: parsedData.success ? parsedData.data : {},
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const session = useCurrentSession()
  const legalEntityMatcher = /^legal_entity_\d+$/
  const legalEntityKeys = Object.keys(data.answers).filter((key) => legalEntityMatcher.test(key))
  const legalEntityCodes = legalEntityKeys.map((key) => data.answers[key])

  const getCompanyCodes = (): string[] => {
    if (data.answers.company_code_enabled === 'true') {
      return data.answers.company ? [data.answers.company] : []
    }

    return legalEntityCodes.filter(Boolean)
  }

  const { loading, data: workplaceData } = useQuery(A3InnuvaWorkplaceStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      organizationalUnitId: data.answers.organizational_unit_id,
      companyCodes: getCompanyCodes(),
    },
  })
  const legalEntities = workplaceData?.companies.legalEntitiesConnection.nodes ?? []
  const a3innuvaWorkplaceCodes = workplaceData?.payrollIntegrationsA3innuva.workplaceCodes ?? []
  const options = workplacesToOptions(a3innuvaWorkplaceCodes)

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        workplaces: {
          ...JSON.parse(JSON.stringify(workplaces)),
          source: 'employee_group',
        },
      },
    })
  })

  const trackEvent = () => {
    let workplacesValues = []
    if (data.answers.workplace_code_legal_entity_selected === 'true') {
      workplacesValues = legalEntityCodes
    } else {
      const locationKeys = Object.keys(data.answers).filter((key) => /^location_\d+$/.test(key))
      workplacesValues = locationKeys.map((key) => data.answers[key])
    }
    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsWorkplacesSelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      workplaces: workplacesValues,
    })
  }

  const updateMappings = (newMappings: A3InnuvaMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        workplaces: {
          source: data.sources?.workplaces?.source,
          mappings: newMappings,
        },
      },
    })
  }

  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.workplace.title'
          )}
        />

        {!loading ? (
          <Box justifyContent="spaceBetween" flexDirection="column" gap="s32">
            <Box paddingBottom="s40">
              {
                <EmployeeGroupsByLegalEntity
                  data={data}
                  onMappingChanged={updateMappings}
                  options={options}
                  legalEntities={legalEntities}
                />
              }
            </Box>
          </Box>
        ) : (
          <Spinner />
        )}
      </Box>
    </Box>
  )
}

export const workplaceStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.workplace.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <WorkplaceStep {...props} />,
  isCompleted: (data) => handleIsCompleted(data),
})

const handleIsCompleted = (data: A3InnuvaWizardData) => {
  const workplaces = a3InnuvaSourceSchema.safeParse(data.sources?.workplaces)
  const mappings = workplaces?.data?.mappings || []

  if (mappings.length === 0) {
    return false
  }
  const parsedData = a3InnuvaSourceSchema.safeParse(workplaces)

  return parsedData.success
}
